import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ModeEdit from "@mui/icons-material/ModeEdit";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  EpisodicContainerRemovalKind,
  EpisodicEventItemFragment,
  EpisodicManifestExemptionState,
} from "generated-graphql/graphql";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { Fragment, useState } from "react";

import { formatContainerDate, formatQuantity } from "../Containers/containerDisplay";
import { WasteContainerDrawer } from "../Containers/WasteContainerDrawer";
import { AddEventManifestDialog } from "./AddEventManifestDialog";
import { EPISODIC_EVENT_MANIFESTS_QUERY } from "./api";
import { EXEMPTION_STATE_DISPLAY, formatPounds } from "./episodicDisplay";
import {
  CalculatedFields,
  calculatedOf,
  EventStreamRow,
  eventStreamRows,
  exemptCellText,
  notAppliedReason,
  pendingShipmentCopy,
  RowAction,
  rowActions,
  ShippedContainer,
  streamNotes,
  unmatchedShippedContainers,
} from "./eventManifestRows";
import { EventSection } from "./EventSection";
import { EventManifestExemptions, ExemptStream } from "./manifestExemptionForm";
import { ConfirmTarget, removeConfirmFor, useRemoveExemption } from "./removeExemption";

const COLUMN_COUNT = 7;

const isVanished = (stream: ExemptStream) =>
  stream.state === EpisodicManifestExemptionState.StreamNotOnManifest;

export function sectionCaption(exemptions: EventManifestExemptions): string {
  const counted = exemptions.manifests.reduce(
    (sum, manifest) => sum + manifest.streams.filter((stream) => !isVanished(stream)).length,
    0
  );
  const manifests = exemptions.manifests.filter((manifest) =>
    manifest.streams.some((stream) => !isVanished(stream))
  ).length;
  const gone = exemptions.manifests.length - manifests;
  const base = `${counted === 1 ? "1 waste stream" : `${counted} waste streams`} on ${
    manifests === 1 ? "1 manifest" : `${manifests} manifests`
  }`;
  const goneNote = gone > 0 ? ` (+${gone} no longer at this facility)` : "";
  const split =
    exemptions.calculatedExemptPounds > 0
      ? ` · ${formatPounds(exemptions.calculatedExemptPounds)} from containers, ${formatPounds(exemptions.declaredExemptPounds)} declared`
      : "";
  return `${base}${goneNote}${split}`;
}

/** The section subtitle: the excluded pounds first, then the EPA estimate and
 * filing chip when they exist, then the stream and manifest count. */
function SummaryLine({ exemptions }: { exemptions: EventManifestExemptions }) {
  return (
    <Stack spacing={0.25}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {formatPounds(exemptions.totalExemptPounds)} excluded from generator status
        </Typography>
        {exemptions.epaEstimatedPounds != null && (
          <Typography variant="body2" color="text.secondary">
            · {formatPounds(exemptions.epaEstimatedPounds)} estimated on Form 8700-12
          </Typography>
        )}
        {exemptions.filedWithEpa && (
          <Chip size="small" color="success" variant="outlined" label="Filed with EPA" />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {sectionCaption(exemptions)}
      </Typography>
    </Stack>
  );
}

function StatusCell({ row }: { row: EventStreamRow }) {
  const display = EXEMPTION_STATE_DISPLAY[row.state];
  const reason = notAppliedReason(row);
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      color={display.applied ? "success" : "default"}
      label={display.label}
      // Focusable so the reason's tooltip reaches keyboard users too.
      tabIndex={reason ? 0 : undefined}
    />
  );
  return (
    <TableCell>
      {reason ? (
        <Tooltip title={reason} describeChild>
          {chip}
        </Tooltip>
      ) : (
        chip
      )}
    </TableCell>
  );
}

/** On a phone the Status column is gone, so a row that applies nothing keeps its reason on the dash. */
function ExemptCell({ row, phone }: { row: EventStreamRow; phone: boolean }) {
  const reason = phone ? notAppliedReason(row) : null;
  return (
    <TableCell align="right">
      {reason ? (
        <Tooltip title={reason} describeChild>
          <span tabIndex={0}>{exemptCellText(row)}</span>
        </Tooltip>
      ) : (
        exemptCellText(row)
      )}
    </TableCell>
  );
}

function StreamDetails({ row }: { row: EventStreamRow }) {
  return (
    <>
      {row.wasteStream.name}
      {streamNotes(row).map((note) => (
        <Typography key={note} variant="caption" color="text.secondary" component="div">
          {note}
        </Typography>
      ))}
    </>
  );
}

// A disabled button fires no hover events, so each tooltip wraps a span.
function ActionButton({
  action,
  readOnly,
  onAction,
}: {
  action: RowAction;
  readOnly: boolean;
  onAction: (action: RowAction) => void;
}) {
  switch (action) {
    case "edit":
      return (
        <Tooltip title="Edit pounds">
          <span>
            <IconButton
              size="small"
              aria-label="Edit pounds"
              onClick={() => onAction(action)}
              disabled={readOnly}
            >
              <ModeEdit fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    case "remove":
      return (
        <Tooltip title="Remove">
          <span>
            <IconButton
              size="small"
              color="error"
              aria-label="Remove"
              onClick={() => onAction(action)}
              disabled={readOnly}
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
  }
}

const DRILL_DOWN_CELL = { border: 0, py: 0.25, pl: 0, pr: 4 };

function ContainerDrillDown({
  calculated,
  onViewContainer,
}: {
  calculated: CalculatedFields;
  onViewContainer: (containerId: string) => void;
}) {
  const unweighed = calculated.unweighedContainerCount;
  return (
    <Table size="small" aria-label="Containers on this stream" sx={{ width: "auto" }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ ...DRILL_DOWN_CELL, color: "text.secondary" }}>Container</TableCell>
          <TableCell sx={{ ...DRILL_DOWN_CELL, color: "text.secondary" }}>Quantity</TableCell>
          <TableCell sx={{ ...DRILL_DOWN_CELL, color: "text.secondary" }}>Weight</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {calculated.containers.map(({ container, quantity, unit, pounds }) => (
          <TableRow key={container.id}>
            <TableCell sx={DRILL_DOWN_CELL}>
              <Link
                component="button"
                variant="body2"
                onClick={() => onViewContainer(container.id)}
              >
                {container.containerLabel}
              </Link>
            </TableCell>
            <TableCell sx={DRILL_DOWN_CELL}>{formatQuantity(quantity, unit)}</TableCell>
            <TableCell sx={{ ...DRILL_DOWN_CELL, color: pounds == null ? "text.secondary" : "" }}>
              {pounds == null ? "Weight unknown" : formatPounds(pounds)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell sx={{ ...DRILL_DOWN_CELL, color: "text.secondary" }}>
            Container total
          </TableCell>
          <TableCell sx={DRILL_DOWN_CELL} />
          <TableCell sx={{ ...DRILL_DOWN_CELL, fontWeight: 600 }}>
            {formatPounds(calculated.pounds)}
            {unweighed > 0 && (
              <Typography component="span" variant="body2" color="text.secondary">
                {` + ${unweighed} unweighed`}
              </Typography>
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

function ManifestNumberCell({
  row,
  phone,
  expanded,
  alignWithChevrons,
  onToggle,
}: {
  row: EventStreamRow;
  phone: boolean;
  expanded: boolean;
  alignWithChevrons: boolean;
  onToggle: () => void;
}) {
  const gone = row.state === EpisodicManifestExemptionState.StreamNotOnManifest;
  return (
    <TableCell sx={{ color: gone ? "text.disabled" : undefined, whiteSpace: "nowrap" }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {calculatedOf(row.basis) ? (
          <IconButton
            size="small"
            aria-label={expanded ? "Hide containers" : "Show containers"}
            aria-expanded={expanded}
            onClick={onToggle}
            sx={{ p: 0.25 }}
          >
            <ChevronRight
              fontSize="small"
              sx={{
                transform: expanded ? "rotate(90deg)" : "none",
                transition: "transform 150ms",
              }}
            />
          </IconButton>
        ) : (
          alignWithChevrons && <Box sx={{ width: 24, flex: "none" }} />
        )}
        {phone ? (
          <Box>
            {row.manifestTrackingNumber}
            <Typography variant="caption" color="text.secondary" component="div">
              {formatContainerDate(row.shippedDate)}
            </Typography>
            <Box sx={{ whiteSpace: "normal", mt: 0.5 }}>
              <StreamDetails row={row} />
            </Box>
          </Box>
        ) : (
          <span>{row.manifestTrackingNumber}</span>
        )}
      </Stack>
    </TableCell>
  );
}

function ManifestStreamRow({
  row,
  phone,
  readOnly,
  alignWithChevrons,
  onAction,
  onViewContainer,
}: {
  row: EventStreamRow;
  /** Ship date and stream stack under the manifest number; On manifest and Status are dropped. */
  phone: boolean;
  readOnly: boolean;
  alignWithChevrons: boolean;
  onAction: (action: RowAction) => void;
  onViewContainer: (containerId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const gone = row.state === EpisodicManifestExemptionState.StreamNotOnManifest;
  const calculated = calculatedOf(row.basis);
  return (
    <>
      <TableRow>
        <ManifestNumberCell
          row={row}
          phone={phone}
          expanded={expanded}
          alignWithChevrons={alignWithChevrons}
          onToggle={() => setExpanded((current) => !current)}
        />
        {!phone && (
          <>
            <TableCell>{formatContainerDate(row.shippedDate)}</TableCell>
            <TableCell>
              <StreamDetails row={row} />
            </TableCell>
            <TableCell align="right">{gone ? "—" : formatPounds(row.manifestPounds)}</TableCell>
          </>
        )}
        <ExemptCell row={row} phone={phone} />
        {!phone && <StatusCell row={row} />}
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            {rowActions(row, readOnly).map((action) => (
              <ActionButton key={action} action={action} readOnly={readOnly} onAction={onAction} />
            ))}
          </Stack>
        </TableCell>
      </TableRow>
      {calculated && expanded && (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT} sx={{ bgcolor: "action.hover", pl: 6 }}>
            <ContainerDrillDown calculated={calculated} onViewContainer={onViewContainer} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function PendingShipmentHint({
  count,
  named,
  onViewContainer,
}: {
  count: number;
  named: ShippedContainer[] | null;
  onViewContainer: (containerId: string) => void;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <InfoOutlined fontSize="small" color="info" />
      <Typography variant="body2" color="text.secondary">
        {pendingShipmentCopy(count)}
        {named && named.length > 0 && (
          <>
            {" ("}
            {named.map((container, index) => (
              <Fragment key={container.id}>
                {index > 0 && ", "}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => onViewContainer(container.id)}
                  sx={{ verticalAlign: "baseline" }}
                >
                  {container.label}
                </Link>
              </Fragment>
            ))}
            {")"}
          </>
        )}
      </Typography>
    </Stack>
  );
}

type AmountDialogTarget = {
  manifestTrackingNumber: string | null;
  overrideStreamId: string | null;
  title?: string;
};

function shippedContainers(event: EpisodicEventItemFragment): ShippedContainer[] {
  return event.containerHistory
    .filter((record) => record.lastRemoval === EpisodicContainerRemovalKind.Shipped)
    .map((record) => ({ id: record.container.id, label: record.container.containerLabel }));
}

function ManifestsTable({
  rows,
  readOnly,
  onAction,
  onViewContainer,
}: {
  rows: EventStreamRow[];
  readOnly: boolean;
  onAction: (row: EventStreamRow, action: RowAction) => void;
  onViewContainer: (containerId: string) => void;
}) {
  const anyContainers = rows.some((row) => calculatedOf(row.basis) !== null);
  const phone = useMediaQuery(useTheme().breakpoints.down("sm"));
  return (
    <TableContainer>
      <Table size="small" sx={phone ? { "& .MuiTableCell-root": { px: 1 } } : undefined}>
        <TableHead>
          <TableRow>
            <TableCell>Manifest #</TableCell>
            {!phone && (
              <>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Ship date</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Waste stream</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  On manifest
                </TableCell>
              </>
            )}
            <TableCell align="right">Exempt</TableCell>
            {!phone && <TableCell>Status</TableCell>}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <ManifestStreamRow
              key={`${row.manifestTrackingNumber}:${row.wasteStream.id}`}
              row={row}
              phone={phone}
              readOnly={readOnly}
              alignWithChevrons={anyContainers}
              onAction={(action) => onAction(row, action)}
              onViewContainer={onViewContainer}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ManifestsBody({
  event,
  rows,
  unmatchedCount,
  onViewContainer,
  ...tableProps
}: {
  event: EpisodicEventItemFragment;
  rows: EventStreamRow[];
  unmatchedCount: number;
  readOnly: boolean;
  onAction: (row: EventStreamRow, action: RowAction) => void;
  onViewContainer: (containerId: string) => void;
}) {
  return (
    <Stack spacing={1.5}>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No manifests carry waste from this event yet. Pounds tied to this event leave generator
          status for the month they shipped.
        </Typography>
      )}
      {rows.length > 0 && (
        <ManifestsTable rows={rows} onViewContainer={onViewContainer} {...tableProps} />
      )}
      {unmatchedCount > 0 && (
        <PendingShipmentHint
          count={unmatchedCount}
          named={unmatchedShippedContainers(shippedContainers(event), rows, unmatchedCount)}
          onViewContainer={onViewContainer}
        />
      )}
    </Stack>
  );
}

function editDialogFor(row: EventStreamRow): AmountDialogTarget {
  return {
    manifestTrackingNumber: row.manifestTrackingNumber,
    // A container-only row opens with its container total typed in, so saving a changed amount declares it.
    overrideStreamId: row.basis.kind === "calculated" ? row.wasteStream.id : null,
    title: "Edit pounds",
  };
}

export function EventManifestsSection({
  event,
  collapsible = true,
}: {
  event: EpisodicEventItemFragment;
  /** Off when this is the card's only section, so there is nothing to fold away. */
  collapsible?: boolean;
}) {
  const { data, error } = useQuery(EPISODIC_EVENT_MANIFESTS_QUERY, {
    variables: { id: event.id },
  });
  const exemptions = data?.episodicEvent?.manifestExemptions ?? null;
  const [amountDialog, setAmountDialog] = useState<AmountDialogTarget | null>(null);
  const [confirm, setConfirm] = useState<ConfirmTarget | null>(null);
  const [viewContainerId, setViewContainerId] = useState<string | null>(null);
  const { isReadOnly } = useFacilityReadOnly(event.facilityId);
  const mutations = useRemoveExemption();

  const rows = exemptions ? eventStreamRows(exemptions) : [];

  return (
    <EventSection
      title="Manifests"
      collapsible={collapsible}
      caption={exemptions ? <SummaryLine exemptions={exemptions} /> : "Loading manifests…"}
      actions={
        <Button
          size="small"
          variant="outlined"
          startIcon={<Add />}
          onClick={() => setAmountDialog({ manifestTrackingNumber: null, overrideStreamId: null })}
          disabled={!exemptions || isReadOnly}
        >
          Add a manifest
        </Button>
      }
    >
      {error && (
        <Typography variant="body2" color="error">
          The manifests could not be loaded.
        </Typography>
      )}
      {exemptions && (
        <ManifestsBody
          event={event}
          rows={rows}
          unmatchedCount={exemptions.unmatchedShippedContainerCount}
          readOnly={isReadOnly}
          onAction={(row, action) =>
            action === "edit"
              ? setAmountDialog(editDialogFor(row))
              : setConfirm(removeConfirmFor(row))
          }
          onViewContainer={setViewContainerId}
        />
      )}

      {exemptions && amountDialog && (
        <AddEventManifestDialog
          eventId={event.id}
          startDate={event.startDate}
          exemptions={exemptions}
          reliefApplies={event.relief.isAvailable}
          initialManifestTrackingNumber={amountDialog.manifestTrackingNumber}
          overrideStreamId={amountDialog.overrideStreamId}
          title={amountDialog.title}
          open
          onClose={() => setAmountDialog(null)}
        />
      )}

      {viewContainerId && (
        <WasteContainerDrawer
          containerId={viewContainerId}
          open
          onClose={() => setViewContainerId(null)}
        />
      )}

      <ConfirmDialog
        open={confirm != null}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm) {
            void mutations.remove(confirm, () => setConfirm(null));
          }
        }}
        loading={mutations.removing}
        header={confirm?.header ?? ""}
        info={confirm?.info ?? ""}
        confirmText={confirm?.confirmText ?? "Remove"}
      />
    </EventSection>
  );
}
